import type { ReactElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';

/**
 * Mounts an extracted React screen the way a legacy screen function paints:
 * wipe #app, then render into it — React renders straight into #app so the
 * painted DOM matches the legacy tree exactly (no wrapper element).
 *
 * Legacy screens and React screens share #app, so exactly one React root
 * exists at a time and it is unmounted before anything else paints: every
 * navigation away from an extracted screen goes through a React handler,
 * which unmounts (directly or by mounting the next screen) before
 * delegating to legacy. Legacy overlays appended into #app while a React
 * screen is mounted are foreign nodes React neither owns nor removes.
 */
let activeRoot: Root | null = null;

export function mountExtractedScreen(screen: ReactElement): void {
  unmountExtractedScreen();
  const app = document.getElementById('app');
  if (!app) throw new Error('The #app viewport is missing');
  app.innerHTML = '';
  activeRoot = createRoot(app);
  activeRoot.render(screen);
}

export function unmountExtractedScreen(): void {
  if (!activeRoot) return;
  const root = activeRoot;
  activeRoot = null;
  root.unmount();
}
